/**
 * BIK Policy Info Component
 * 
 * Reusable component for displaying BIK policy information consistently.
 * Used to avoid duplication of policy text across BIK management tabs.
 */

import React, { useState } from 'react';
import { Info, ChevronDown, ChevronUp } from 'lucide-react';
import {
  getBIKPolicyDescription,
  getBIKFormulaText,
  formatBIKRateAsPercentage,
  formatBIKRateAsFraction,
} from '../../../../constants/bikConstants';

const BIKPolicyInfo = ({ 
  title = 'Uniform BIK Policy',
  variant = 'blue', // 'blue' | 'gray'
  expandable = false,
  showFormula = true,
  children 
}) => {
  const [isExpanded, setIsExpanded] = useState(!expandable);
  
  const variantClasses = {
    blue: {
      container: 'bg-blue-50 border-blue-200',
      icon: 'bg-blue-200 text-blue-600',
      title: 'text-blue-800',
      text: 'text-blue-700',
    },
    gray: {
      container: 'bg-gray-50 border-gray-200',
      icon: 'bg-gray-200 text-gray-600',
      title: 'text-gray-700',
      text: 'text-gray-600',
    },
  };
  
  const classes = variantClasses[variant];
  
  return (
    <div className={`${classes.container} border rounded-2xl p-4`}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 ${classes.icon} rounded-full flex items-center justify-center flex-shrink-0`}>
          <Info className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className={`text-sm font-semibold truncate min-w-0 ${classes.title}`}>
              {title}
            </h3>
            {expandable && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
            )}
          </div>
          
          {isExpanded && (
            <div className="mt-1 space-y-2">
              <p className={`text-sm ${classes.text}`}>
                All private vehicles (S plates) use the <strong>{getBIKPolicyDescription()}</strong> of running costs.
                BIK is calculated automatically based on Asset Portal data - no manual configuration required.
              </p>
              
              {showFormula && (
                <p className={`text-xs ${classes.text} mt-2 pt-2 border-t ${variant === 'blue' ? 'border-blue-200' : 'border-gray-200'}`}>
                  <strong>Formula:</strong> {getBIKFormulaText()}
                </p>
              )}
              
              {children}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BIKPolicyInfo;
